/**
 * Dashboard Header Component
 * Top header with search, language switcher, theme toggle, notifications, and user menu
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Button,
  InputBase,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  ListItemIcon,
  Tooltip,
  useTheme as useMuiTheme,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  KeyboardArrowDown as ArrowDownIcon,
  FormatTextdirectionLToR as LtrIcon,
  FormatTextdirectionRToL as RtlIcon,
  QrCodeScanner as QrCodeScannerIcon,
} from '@mui/icons-material';

import { useAuth } from '../../store/AuthContext';
import { useTheme } from '../../store/ThemeContext';
import { useDirection } from '../../store/DirectionContext';
import { languages, type LanguageCode } from '../../i18n';
import { getApiOrigin } from '../../services/api';
import VerifyDocumentDialog from '../qr/VerifyDocumentDialog';
import { logQrScan } from '../../services/eventLogs';
import type { VerificationResult } from '../../utils/documentVerification';
import { NetworkStatusIndicator } from '../common/NetworkStatusIndicator';
import RemittanceRemindersBell from '../finance/RemittanceRemindersBell';

interface DashboardHeaderProps {
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}

export function DashboardHeader({
  onToggleSidebar,
}: DashboardHeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const { user, logout } = useAuth();
  const { mode, toggleTheme, colors } = useTheme();
  const { direction, toggleDirection } = useDirection();

  const [languageAnchor, setLanguageAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const [qrVerifyOpen, setQrVerifyOpen] = useState(false);

  // Log an authenticated QR verify scan triggered from the top header.
  // Fire-and-forget: the scan flow is not blocked if the log call fails.
  const handleHeaderScanComplete = (
    decodedText: string,
    result: VerificationResult,
  ) => {
    void logQrScan({
      event_type: 'QR_VERIFY_SCAN_DASHBOARD',
      document_reference: result.reference ?? null,
      document_type: result.documentType ?? null,
      description: 'QR code scanned from the dashboard top header',
      event_metadata: {
        source: 'dashboard_header',
        signature_valid: result.valid,
        error: result.error ?? null,
        raw_length: decodedText.length,
      },
    });
  };

  const currentLanguage = languages[i18n.language as LanguageCode] || languages.fr;

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleLanguageSelect = (langCode: LanguageCode) => {
    void i18n.changeLanguage(langCode);
    handleLanguageClose();
  };

  const handleUserClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchor(event.currentTarget);
  };

  const handleUserClose = () => {
    setUserAnchor(null);
  };

  const handleProfile = () => {
    handleUserClose();
    // Navigate to the edit user page for the connected user
    if (user?.structure_id && user?.id) {
      navigate(`/dashboard/transporters/${user.structure_id}/users/${user.id}/edit`);
    } else {
      navigate('/dashboard/profile');
    }
  };

  // Get user avatar URL
  const getUserAvatarUrl = () => {
    if (user?.avatar) {
      return `${getApiOrigin()}${user.avatar}`;
    }
    return undefined;
  };

  const handleSettings = () => {
    handleUserClose();
    navigate('/dashboard/settings');
  };

  const handleLogout = async () => {
    handleUserClose();
    await logout();
    navigate('/login');
  };

  // Generate user initials
  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.username || user?.email || 'User';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: muiTheme.palette.background.paper,
        borderBottom: `1px solid ${muiTheme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ gap: 1, py: 0.5 }}>
        {/* Menu toggle button */}
        <IconButton
          onClick={onToggleSidebar}
          sx={{ color: muiTheme.palette.text.primary }}
        >
          <MenuIcon />
        </IconButton>

        {/* Search bar */}
        {/* <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: alpha(muiTheme.palette.text.primary, 0.05),
            borderRadius: 2,
            px: 2,
            py: 0.5,
            flexGrow: 1,
            maxWidth: { xs: '100%', sm: 400 },
          }}
        >
          <SearchIcon sx={{ color: muiTheme.palette.text.secondary, mr: 1 }} />
          <InputBase
            placeholder={t('common.search') + '...'}
            sx={{
              flexGrow: 1,
              '& input': {
                py: 0.5,
                fontSize: '0.9rem',
              },
            }}
          />
        </Box> */}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Network Status */}
        <NetworkStatusIndicator />

        {/* QR Code Document Verification */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => setQrVerifyOpen(true)}
          sx={{
            borderColor: muiTheme.palette.divider,
            color: muiTheme.palette.text.primary,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: muiTheme.palette.primary.main,
              backgroundColor: alpha(muiTheme.palette.primary.main, 0.05),
            },
          }}
        >
          <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('common.documentVerification.title')}
          </Box>
        </Button>

        {/* Language Selector */}
        <Box>
          <Tooltip title={t('common.changeLanguage')}>
            <Box
              onClick={handleLanguageClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(muiTheme.palette.text.primary, 0.05),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: muiTheme.palette.text.primary,
                  fontWeight: 500,
                }}
              >
                {currentLanguage.name}
              </Typography>
              <ArrowDownIcon
                sx={{
                  fontSize: 18,
                  color: muiTheme.palette.text.secondary,
                }}
              />
            </Box>
          </Tooltip>
          <Menu
            anchorEl={languageAnchor}
            open={Boolean(languageAnchor)}
            onClose={handleLanguageClose}
            PaperProps={{
              sx: { minWidth: 150 },
            }}
          >
            {Object.entries(languages).map(([code, lang]) => (
              <MenuItem
                key={code}
                onClick={() => handleLanguageSelect(code as LanguageCode)}
                selected={i18n.language === code}
              >
                <Typography variant="body2">{lang.name}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* RTL/LTR Toggle */}
        <Tooltip title={direction === 'ltr' ? t('settings.rtl') : t('settings.ltr')}>
          <IconButton
            onClick={toggleDirection}
            sx={{ color: muiTheme.palette.text.primary }}
          >
            {direction === 'ltr' ? <RtlIcon /> : <LtrIcon />}
          </IconButton>
        </Tooltip>

        {/* Theme Toggle */}
        <Tooltip title={mode === 'light' ? t('common.darkMode') : t('common.lightMode')}>
          <IconButton
            onClick={toggleTheme}
            sx={{ color: muiTheme.palette.text.primary }}
          >
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>

        {/* Notifications (remittance reminders) */}
        <RemittanceRemindersBell />

        {/* User Menu */}
        <Box>
          <Tooltip title={t('dashboard.profile')}>
            <Box
              onClick={handleUserClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(muiTheme.palette.text.primary, 0.05),
                },
              }}
            >
              <Avatar
                src={getUserAvatarUrl()}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: colors.secondary.main,
                  color: colors.secondary.contrastText,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {getUserInitials()}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: muiTheme.palette.text.primary,
                    lineHeight: 1.2,
                  }}
                >
                  {getUserDisplayName()}
                </Typography>
              </Box>
              <ArrowDownIcon
                sx={{
                  fontSize: 18,
                  color: muiTheme.palette.text.secondary,
                  display: { xs: 'none', sm: 'block' },
                }}
              />
            </Box>
          </Tooltip>
          <Menu
            anchorEl={userAnchor}
            open={Boolean(userAnchor)}
            onClose={handleUserClose}
            PaperProps={{
              sx: { minWidth: 200, mt: 1 },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={getUserAvatarUrl()}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: colors.secondary.main,
                  color: colors.secondary.contrastText,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {getUserInitials()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {getUserDisplayName()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: muiTheme.palette.text.secondary }}
                >
                  {user?.email || user?.phone}
                </Typography>
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t('nav.profile')}
            </MenuItem>
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t('nav.settings')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* QR Code Verification Dialog */}
      <VerifyDocumentDialog
        open={qrVerifyOpen}
        onClose={() => setQrVerifyOpen(false)}
        onScanComplete={handleHeaderScanComplete}
      />
    </AppBar>
  );
}

export default DashboardHeader;
